import { updateBusinessEntity } from '@commenda/api';
import {
  Button,
  DateField,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Label,
  LoadingIndicator,
  useToggle,
} from '@commenda/kakashi';
import { IBusinessEntity } from '@commenda/types';
import { useEffect, useState } from 'react';
import { useNotification } from '../../../../../hooks/useNotification';

export const EditDateOfClosureModal = ({
  isOpen,
  onClose,
  businessEntity,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  businessEntity: IBusinessEntity;
  onSuccess?: () => void;
}) => {
  const isSubmitting = useToggle();

  const existingDateOfClosure =
    businessEntity.generalCorporationInfo?.dateOfClosure ?? '';

  const [dateOfClosure, setDateOfClosure] = useState(existingDateOfClosure);

  useEffect(() => {
    setDateOfClosure(existingDateOfClosure);
  }, [existingDateOfClosure, isOpen]);

  const { setNotification } = useNotification();

  const onSave = async () => {
    isSubmitting.on();

    const res = await updateBusinessEntity(businessEntity.id, {
      dateOfClosure,
    });

    if (!res.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to update date of closure',
      });
      isSubmitting.off();
      return;
    }

    setNotification({
      show: true,
      valid: true,
      title: 'Success',
      message: 'Date of closure updated',
    });

    if (onSuccess) await onSuccess();
    isSubmitting.off();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent preventCloseOnOutsideClick>
        <DialogHeader>
          <DialogTitle>
            {existingDateOfClosure ? 'Edit' : 'Add'} date of closure
          </DialogTitle>
        </DialogHeader>
        <div className="p-3xl">
          <FieldWrapper>
            <Label htmlFor="dateOfClosure">Date of closure</Label>
            <DateField
              value={dateOfClosure}
              onChange={(value) => setDateOfClosure(value ?? '')}
              disallowFutureDates
              fullWidth
            />
          </FieldWrapper>
        </div>
        <DialogFooter>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={onSave}
            disabled={
              isSubmitting.value ||
              !dateOfClosure ||
              dateOfClosure === existingDateOfClosure
            }
          >
            {isSubmitting.value ? <LoadingIndicator size={16} /> : null}
            <span>Save</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
